import React, {useEffect, useState} from 'react';
import './AdminFriendReferalConfigPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import RewardListEditor, {NUMERIC_REWARD_TYPES} from '../../elements/RewardListEditor/RewardListEditor';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';

const PAGE_ID = 'AdminFriendReferalConfigPage';

const EMPTY_REWARD = {itemType: INVENTORY_ITEMS.NUMENS, value: ''};

// Награды из конфига (числа) → строки для полей ввода
const toEditableRewards = (rewards) => (
  rewards?.length
    ? rewards.map((reward) => ({itemType: reward.itemType, value: String(reward.value)}))
    : [{...EMPTY_REWARD}]
);

/**
 * Админка, раздел «Приглашение друзей»: глобальные награды программы — другу
 * за регистрацию по ссылке и пригласившему за каждого друга. Редактирование —
 * как у наград рефералам на странице партнера. Полностью пустой список
 * (одна пустая строка) сохраняется как «награды отключены».
 */
function AdminFriendReferalConfigPage({onBack}) {
  usePageAnalytics('AdminFriendReferalConfigPage');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [friendRewards, setFriendRewards] = useState([{...EMPTY_REWARD}]);
  const [inviterRewards, setInviterRewards] = useState([{...EMPTY_REWARD}]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [savedMessage, setSavedMessage] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/friend-referal/config`, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Не удалось загрузить настройки');
        }
        setFriendRewards(toEditableRewards(data.friendRewards));
        setInviterRewards(toEditableRewards(data.inviterRewards));
      } catch (e) {
        console.error('Friend referal config error', e);
        setLoadError(e.message || 'Не удалось загрузить настройки');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Пустые строки отбрасываются (полностью пустой список = награды
  // отключены); частично заполненная строка — ошибка
  const collectRewards = (rewards, sectionName) => {
    const filled = rewards.filter((reward) => String(reward.value).trim());
    for (const reward of filled) {
      if (NUMERIC_REWARD_TYPES.includes(reward.itemType)) {
        const amount = parseInt(reward.value, 10);
        if (!Number.isInteger(amount) || amount <= 0) {
          return {error: `${sectionName}: количество должно быть целым положительным числом`};
        }
      }
    }
    return {rewards: filled.map((reward) => ({itemType: reward.itemType, value: String(reward.value).trim()}))};
  };

  const handleSave = async () => {
    if (saving) return;
    setSavedMessage(null);

    const friendCollected = collectRewards(friendRewards, 'Награды другу');
    if (friendCollected.error) {
      setFormError(friendCollected.error);
      return;
    }
    const inviterCollected = collectRewards(inviterRewards, 'Награды пригласившему');
    if (inviterCollected.error) {
      setFormError(inviterCollected.error);
      return;
    }
    setFormError(null);

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/friend-referal/config`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          friendRewards: friendCollected.rewards,
          inviterRewards: inviterCollected.rewards,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось сохранить настройки');
      }
      setFriendRewards(toEditableRewards(data.friendRewards));
      setInviterRewards(toEditableRewards(data.inviterRewards));
      setSavedMessage('Настройки сохранены');
    } catch (e) {
      console.error('Save friend referal config error', e);
      setFormError(e.message || 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className="lobby-page-title">Приглашение друзей</h1>

      {loading && <p className="admin-friend-config-status">Загрузка...</p>}
      {loadError && <p className="admin-friend-config-status admin-friend-config-status_error">{loadError}</p>}

      {!loading && !loadError && (
        <div className="admin-friend-config-form">
          <h2 className="admin-friend-config-section-title">Награды другу за регистрацию</h2>
          <RewardListEditor rewards={friendRewards} onChange={setFriendRewards} disabled={saving}/>

          <h2 className="admin-friend-config-section-title">Награды пригласившему за каждого друга</h2>
          <RewardListEditor rewards={inviterRewards} onChange={setInviterRewards} disabled={saving}/>

          {formError && <p className="admin-friend-config-status admin-friend-config-status_error">{formError}</p>}
          {savedMessage &&
            <p className="admin-friend-config-status admin-friend-config-status_success">{savedMessage}</p>}
        </div>
      )}

      <div className="main-menu-buttons admin-friend-config-buttons">
        {!loading && !loadError && (
          <PageButton onClick={handleSave} pageId={PAGE_ID} buttonId="save" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </PageButton>
        )}
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default AdminFriendReferalConfigPage;
